import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Row } from 'reactstrap';
import styled from 'styled-components';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import itLocale from 'date-fns/locale/it';
import 'react-datepicker/dist/react-datepicker.css';
import ExpandIcon from '../../../../components/ExpandIcon';
import ListItem from '../../../../components/ListItem';
import { ExtraReportCharacterNames, USER_ROLES } from '../../../../res/Constants';
import { roleLabels } from '../../../../utils/Utils';
import { ColNoPadding, FieldTitle, VerticalDiv } from './../../../../components/Common';
import InfoText from './../../../../components/InfoText';
import colors from './../../../../res/styles/Colors';
import dimension from './../../../../res/styles/Responsive';
import { toastError } from './../../../../ui/ToastHelper';
import CrAlertItemDetail from './CrAlertItemDetail';

registerLocale('it', itLocale);

export default class CrAlertItem extends PureComponent {
    static propTypes = {
        className: PropTypes.string,
        data: PropTypes.object.isRequired,
        isEditable: PropTypes.bool,
        isNewCrAlert: PropTypes.bool,
        isSelectable: PropTypes.bool,
        isSelected: PropTypes.bool,
        isRead: PropTypes.bool,
        isAdmin: PropTypes.bool,
        usersList: PropTypes.array,
        onItemCanceled: PropTypes.func,
        onItemSelected: PropTypes.func,
        onItemUpdated: PropTypes.func,
        onItemDeleted: PropTypes.func
    };

    static defaultProps = {
        data: {},
        isEditable: false,
        isNewCrAlert: false,
        isSelected: false,
        isRead: false,
        isAdmin: false,
        usersList: [],
        onItemCanceled: () => { },
        onItemSelected: () => { },
        onItemUpdated: () => { },
        onItemDeleted: () => { }
    };

    state = {
        isExpanded: isMobileOnly ? this.props.isEditable : true,
        isEditable: this.props.isEditable,
        print: false,
        data: this.props.data,
        userPk: this.getUserPkFromUsername(this.props.data.username, this.props.usersList),
        date: this.props.data.date || moment()
    };

    getUserPkFromUsername(username, usersList) {
        const user = usersList.find(u => u.username === username);
        return user ? user.pk : '';
    }

    toggleItemDetail = () => {
        this.setState({ isExpanded: !this.state.isExpanded });
    };

    toggleEdit = () => {
        this.setState(
            (prevState, props) => {
                // When canceling edit, reset userPk by finding it from username
                let resetUserPk = prevState.userPk;
                if (prevState.isEditable === true) {
                    const currentUser = props.usersList.find(user => user.username === props.data.username);
                    resetUserPk = currentUser ? currentUser.pk : '';
                }

                return {
                    data: prevState.isEditable === true ? { ...props.data } : { ...prevState.data },
                    userPk: prevState.isEditable === true ? resetUserPk : prevState.userPk,
                    date: prevState.isEditable === true ? props.data.date || moment() : prevState.date,
                    isEditable: !prevState.isEditable
                };
            },
            () => {
                // after the state update
                if (!this.state.isEditable) {
                    this.props.onItemCanceled();
                }
            }
        );
    };

    onFieldEdited = (fieldId, value) => {
        this.setState((state) => {
            const newData = cloneDeep(state.data);
            const textValue = value.toString();
            if (newData.extraReportCharacters[fieldId] == null) {
                // create the relative field if doesn't exist
                newData.extraReportCharacters[fieldId] = {
                    name: fieldId,
                    value: textValue
                };
            } else {
                const characterValue =
                    (fieldId === ExtraReportCharacterNames.NET_HA ||
                        fieldId === ExtraReportCharacterNames.DESTROYED_HA) &&
                        textValue.length === 0
                        ? '0'
                        : textValue;
                newData.extraReportCharacters[fieldId].value = characterValue;
            }
            return {
                data: newData
            };
        });
    };

    onUserEdited = (userPk) => {
        // Find the username for the selected userPk and update state
        const selectedUser = this.props.usersList.find(user => user.pk === parseInt(userPk));
        const username = selectedUser ? selectedUser.username : this.state.data.username;

        this.setState({
            userPk,
            data: {
                ...this.state.data,
                username: username
            }
        });
    };

    onDateEdited = (date) => {
        this.setState({ date });
    };

    onEditingCompleted = () => {
        // Validation for new CR-Alerts
        if (this.props.isNewCrAlert) {
            let hasError = false;

            // Check if username is selected
            if (!this.state.userPk || this.state.userPk === '') {
                toastError('Please select a user');
                hasError = true;
            }

            // Check if date is selected
            if (!this.state.date) {
                toastError('Please select a date');
                hasError = true;
            }

            // If validation fails, don't proceed
            if (hasError) {
                return;
            }
        }

        // Find the username corresponding to the selected userPk
        const selectedUser = this.props.usersList.find(user => user.pk === this.state.userPk);
        const username = selectedUser ? selectedUser.username : this.state.data.username;

        const updatedData = {
            ...this.state.data,
            username: username,
            date: this.state.date
        };

        this.setState({
            isEditable: false,
            data: updatedData
        });

        this.props.onItemUpdated(updatedData);
    };

    render() {
        const expandableChildren = (
            <CrAlertItemDetail
                isEditable={this.state.isEditable}
                toggleEdit={this.toggleEdit}
                onFieldEdited={this.onFieldEdited}
                onEditingCompleted={this.onEditingCompleted}
                onDelete={this.props.onItemDeleted}
                data={this.state.data}
            />
        );

        return (
            <StyledListItem
                expandableChildren={expandableChildren}
                className={this.props.className}
                isExpanded={this.state.isExpanded || !isMobileOnly}
                isSelectable={this.props.isSelectable}
                isSelected={this.props.isSelected}
                isRead={this.props.isRead}
                onSelect={() => this.props.onItemSelected(this.props.data.pk)}>
                {(renderProps) => (
                    <>
                        <Row>
                            <ColNoPadding>
                                <InfoText
                                    title='Techform Code'
                                    content={this.state.data.techFormCode}
                                />
                            </ColNoPadding>
                            <ColNoPadding>
                                <InfoText title='Lot. N°' content={this.state.data.lotNumber} />
                            </ColNoPadding>
                            <ColNoPadding>
                                <EditableUser
                                    isEditable={this.state.isEditable && this.props.isNewCrAlert && this.props.isAdmin}
                                    userPk={this.state.userPk}
                                    username={this.state.data.username}
                                    usersList={this.props.usersList}
                                    onUserChange={this.onUserEdited}
                                />
                            </ColNoPadding>
                            <ColNoPadding className='d-none d-md-block'>
                                <EditableDate
                                    isEditable={this.state.isEditable && this.props.isNewCrAlert && this.props.isAdmin}
                                    date={this.state.date}
                                    onDateChange={this.onDateEdited}
                                />
                            </ColNoPadding>
                            <ColNoPadding xs='1' className='d-md-none'>
                                <ExpandIcon onClick={renderProps.toggleExpand} />
                            </ColNoPadding>
                        </Row>
                        <Row>
                            <ColNoPadding className='d-md-none'>
                                <EditableDate
                                    isEditable={this.state.isEditable && this.props.isNewCrAlert && this.props.isAdmin}
                                    date={this.state.date}
                                    onDateChange={this.onDateEdited}
                                />
                            </ColNoPadding>
                            {this.props.isGeneric && (
                                <>
                                    <ColNoPadding>
                                        <InfoText
                                            title='Customer'
                                            content={this.state.data.clientCode}
                                        />
                                    </ColNoPadding>
                                    <ColNoPadding>
                                        <InfoText
                                            title='Species'
                                            content={`${this.state.data.speciesCode}${this.state.data.varietyCode}`}
                                        />
                                    </ColNoPadding>
                                    <ColNoPadding className='d-none d-md-block'>
                                        <InfoText
                                            title='Variety'
                                            content={this.state.data.varietyName}
                                        />
                                    </ColNoPadding>
                                    <ColNoPadding className='d-none d-md-block'>
                                        <InfoText
                                            title='Grower'
                                            content={this.state.data.growerName}
                                        />
                                    </ColNoPadding>
                                    <ColNoPadding xs='1' className='d-md-none' />
                                </>
                            )}
                        </Row>
                        {this.props.isGeneric && isMobileOnly && (
                            <Row>
                                <ColNoPadding>
                                    <InfoText
                                        title='Variety'
                                        content={this.state.data.varietyName}
                                    />
                                </ColNoPadding>
                                <ColNoPadding>
                                    <InfoText title='Grower' content={this.state.data.growerName} />
                                </ColNoPadding>
                            </Row>
                        )}
                    </>
                )}
            </StyledListItem>
        );
    }
}

const CrAlertDate = ({ date, className }) => {
    const formattedDate = moment(date).format('DD/MMMM/YYYY HH:mm');
    return <InfoText className={className} title='Date' content={formattedDate} />;
};

// Editable User component
const EditableUser = ({ isEditable, userPk, username, usersList, onUserChange }) => {
    if (!isEditable) {
        return <InfoText title='User' content={username} />;
    }

    // Group users by role
    const usersByRole = {
        [USER_ROLES.admin]: [],
        [USER_ROLES.commercial]: [],
        [USER_ROLES.financial]: [],
        [USER_ROLES.tech]: []
    };

    usersList.forEach(user => {
        if (usersByRole[user.role]) {
            usersByRole[user.role].push(user);
        }
    });

    return (
        <StyledVerticalDiv>
            <FieldTitle>User</FieldTitle>
            <StyledSelect
                value={userPk}
                onChange={(e) => onUserChange(e.target.value)}
            >
                {Object.entries(usersByRole)
                    .filter(([role, users]) => users.length > 0)
                    .map(([role, users]) => (
                        <optgroup key={role} label={roleLabels[role]}>
                            {users.map((user) => (
                                <option key={user.pk} value={user.pk}>
                                    {user.username}
                                </option>
                            ))}
                        </optgroup>
                    ))
                }
            </StyledSelect>
        </StyledVerticalDiv>
    );
};

// Editable Date component
const EditableDate = ({ isEditable, date, onDateChange }) => {
    if (!isEditable) {
        return <CrAlertDate date={date} />;
    }

    const dateValue = moment(date).toDate();

    const handleDateChange = (selectedDate) => {
        // Combine selected date with current time
        const now = moment();
        const dateWithCurrentTime = moment(selectedDate)
            .hour(now.hour())
            .minute(now.minute())
            .second(now.second())
            .toDate();
        onDateChange(dateWithCurrentTime);
    };

    return (
        <StyledVerticalDiv>
            <FieldTitle>Date</FieldTitle>
            <CrAlertDatePickerWrapper>
                <ReactDatePicker
                    selected={dateValue}
                    onChange={handleDateChange}
                    dateFormat='dd/MM/yyyy'
                    locale='it'
                />
            </CrAlertDatePickerWrapper>
        </StyledVerticalDiv>
    );
};

const StyledVerticalDiv = styled(VerticalDiv)`
    font-size: 12px;

    @media ${dimension.md} {
        width: inherit;
    }
`;

const StyledSelect = styled.select`
    height: inherit;
    border-radius: 3px;
    text-indent: 0;
    font-size: 12px;
    padding: 2px 4px;
    background-color: white !important;
    border: 1px solid #ced4da;
    max-width: 90%;

    option,
    optgroup {
        background-color: white !important;
        color: black;
    }
`;

const StyledDateInput = styled.input`
    height: inherit;
    border-radius: 3px;
    text-indent: 0;
    font-size: 12px;
    padding: 2px 4px;
    background-color: white !important;
    border: 1px solid #ced4da;
    max-width: 90%;
`;

const CrAlertDatePickerWrapper = styled.div`
    width: 100%;
    max-width: 90%;

    .react-datepicker-wrapper {
        width: 100%;
        display: block;
    }
    
    .react-datepicker__input-container {
        width: 100%;
        display: block;
    }
    
    .react-datepicker__input-container input {
        width: 100%;
        height: inherit;
        border-radius: 3px;
        text-indent: 0;
        font-size: 12px;
        padding: 2px 4px;
        background-color: white !important;
        border: 1px solid #ced4da;
        box-sizing: border-box;

        &:focus {
            outline: none;
        }
    }

    .react-datepicker__navigation-icon::before {
        border-color: #666 !important;
    }

    .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
        border-color: #333 !important;
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--keyboard-selected {
        background-color: ${colors.green} !important;
        color: white !important;
        font-weight: bold;
    }

    .react-datepicker__day--selected:hover,
    .react-datepicker__day--keyboard-selected:hover {
        background-color: ${colors.dark_green} !important;
    }
`;

const StyledListItem = styled(ListItem)`
    & > div:first-child,
    & > div:first-child *:not(.selector) {
        background-color: ${({ isRead }) => (isRead !== true ? colors.light_red : 'white')};
    }

    @media ${dimension.md} {
        & > div div:not(.selector) {
            background-color: ${({ isRead }) => (isRead !== true ? colors.light_red : 'white')};
        }
    }
`;
